import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSdk, translate } from '@fondationcss/sdk';
import { useSdk as useCustomSdk } from '@/composables/use-sdk';
import type { InitializationStepStatus } from '@/sdk/model/initialization-status';

export interface WizardStep {
  key: string;
  title: string;
  description: string;
  icon: string;
  route: string;
  completed: boolean;
  optional: boolean;
}

interface StepDefinition extends Omit<WizardStep, 'completed' | 'title' | 'description'> {
  titleKey: string;
  descriptionKey: string;
}

const STEP_DEFINITIONS: StepDefinition[] = [
  {
    key: 'configure-privacy',
    titleKey: 'parcours.setup.wizard.step.configurePrivacy.title',
    descriptionKey: 'parcours.setup.wizard.step.configurePrivacy.description',
    icon: 'fas fa-shield-halved',
    route: '/gestion/confidentialite/parametres',
    optional: true,
  },
  {
    key: 'define-sites',
    titleKey: 'parcours.setup.wizard.step.defineSites.title',
    descriptionKey: 'parcours.setup.wizard.step.defineSites.description',
    icon: 'fas fa-building',
    route: '/gestion/centres',
    optional: false,
  },
  {
    key: 'define-locals',
    titleKey: 'parcours.setup.wizard.step.defineLocals.title',
    descriptionKey: 'parcours.setup.wizard.step.defineLocals.description',
    icon: 'fas fa-door-open',
    route: '/gestion/locaux-ressources',
    optional: false,
  },
  {
    key: 'define-resources',
    titleKey: 'parcours.setup.wizard.step.defineResources.title',
    descriptionKey: 'parcours.setup.wizard.step.defineResources.description',
    icon: 'fas fa-desktop',
    route: '/gestion/locaux-ressources',
    optional: true,
  },
  {
    key: 'define-calendars',
    titleKey: 'parcours.setup.wizard.step.defineCalendars.title',
    descriptionKey: 'parcours.setup.wizard.step.defineCalendars.description',
    icon: 'fas fa-calendar',
    route: '/core/calendar',
    optional: false,
  },
  {
    key: 'define-schedule-patterns',
    titleKey: 'parcours.setup.wizard.step.defineSchedulePatterns.title',
    descriptionKey: 'parcours.setup.wizard.step.defineSchedulePatterns.description',
    icon: 'fas fa-clock',
    route: '/core/schedule-patterns',
    optional: false,
  },
  {
    key: 'activate-programs',
    titleKey: 'parcours.setup.wizard.step.activatePrograms.title',
    descriptionKey: 'parcours.setup.wizard.step.activatePrograms.description',
    icon: 'fas fa-book-open',
    route: '/gestion/programmes',
    optional: false,
  },
  {
    key: 'sequence-modules',
    titleKey: 'parcours.setup.wizard.step.sequenceModules.title',
    descriptionKey: 'parcours.setup.wizard.step.sequenceModules.description',
    icon: 'fas fa-sort-amount-down',
    route: '/gestion/programmes',
    optional: false,
  },
  {
    key: 'assign-module-resources',
    titleKey: 'parcours.setup.wizard.step.assignModuleResources.title',
    descriptionKey: 'parcours.setup.wizard.step.assignModuleResources.description',
    icon: 'fas fa-link',
    route: '/gestion/programmes',
    optional: true,
  },
  {
    key: 'create-teachers',
    titleKey: 'parcours.setup.wizard.step.createTeachers.title',
    descriptionKey: 'parcours.setup.wizard.step.createTeachers.description',
    icon: 'fas fa-chalkboard-teacher',
    route: '/gestion/enseignants',
    optional: false,
  },
  {
    key: 'assign-teacher-modules',
    titleKey: 'parcours.setup.wizard.step.assignTeacherModules.title',
    descriptionKey: 'parcours.setup.wizard.step.assignTeacherModules.description',
    icon: 'fas fa-user-tag',
    route: '/gestion/enseignants',
    optional: false,
  },
  {
    key: 'plan-group',
    titleKey: 'parcours.setup.wizard.step.planGroup.title',
    descriptionKey: 'parcours.setup.wizard.step.planGroup.description',
    icon: 'fas fa-users',
    route: '/planification/vue-ensemble',
    optional: true,
  },
  {
    key: 'add-frequentation',
    titleKey: 'parcours.setup.wizard.step.addFrequentation.title',
    descriptionKey: 'parcours.setup.wizard.step.addFrequentation.description',
    icon: 'fas fa-user-graduate',
    route: '/planification/etudiants',
    optional: false,
  },
  {
    key: 'plan-individualized',
    titleKey: 'parcours.setup.wizard.step.planIndividualized.title',
    descriptionKey: 'parcours.setup.wizard.step.planIndividualized.description',
    icon: 'fas fa-user-friends',
    route: '/planification/individualise',
    optional: true,
  },
  {
    key: 'record-presences',
    titleKey: 'parcours.setup.wizard.step.recordPresences.title',
    descriptionKey: 'parcours.setup.wizard.step.recordPresences.description',
    icon: 'fas fa-calendar-check',
    route: '/enseignement/presences',
    optional: false,
  },
];

const DISMISSED_STORAGE_KEY = 'parcours.wizard.dismissed';

const showStepsDialog = ref(false);
const dismissed = ref(readDismissedFlag());

function readDismissedFlag(): boolean {
  try {
    return localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function dismissWizard() {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
  } catch {
    // Ignore storage errors — dismissal is a convenience, not critical
  }
  dismissed.value = true;
  showStepsDialog.value = false;
}

export function resetWizardDismissal() {
  try {
    localStorage.removeItem(DISMISSED_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
  dismissed.value = false;
  showStepsDialog.value = true;
}

const ADMIN_PERMISSION = 'Parcours.LearningModule.Administrateur';

/**
 * Who the assistant is for. Configuring the application is administrator work — every step it
 * lists lands on a `/gestion` or `/core` screen — so anyone else has nothing to do with it.
 *
 * Exported on its own so a surface that only needs to decide whether to OFFER the assistant
 * (the profile menu) can ask without instantiating the whole composable, which would fire a
 * second status fetch on every mount.
 */
export function useWizardAudience() {
  const coreSdk = useSdk();
  return computed(() => (coreSdk.me.value?.permissions || []).includes(ADMIN_PERMISSION));
}

export function useInitializationWizard() {
  const customSdk = useCustomSdk();
  const router = useRouter();

  const steps = ref<WizardStep[]>([]);
  const loading = ref(false);
  const loaded = ref(false);
  const showWelcomeDialog = ref(false);

  const isAdmin = useWizardAudience();

  const completedCount = computed(() => steps.value.filter(s => s.completed).length);
  const requiredCount = computed(() => steps.value.filter(s => !s.optional).length);
  const requiredCompletedCount = computed(() => steps.value.filter(s => !s.optional && s.completed).length);
  const totalCount = computed(() => steps.value.length);
  const allCompleted = computed(() => requiredCompletedCount.value === requiredCount.value);
  const anyCompleted = computed(() => completedCount.value > 0);
  // The assistant withdraws on its own once there is nothing left to do. `.docs/INITIALISATION.md`
  // has always said the button shows « tant que toutes les étapes obligatoires ne sont pas
  // complétées », but the condition never tested it: a fully configured instance — a production
  // centre a year into its use, a demonstration with 302 students — still carried a floating
  // button over every page, which only the per-device « Ne plus afficher » could remove. The
  // optional steps deliberately do not hold it open: they are suggestions, and a centre that
  // declines to model its resources would otherwise never be rid of it.
  const showFab = computed(() =>
    isAdmin.value && loaded.value && !dismissed.value && !allCompleted.value);

  function mergeStatus(statusSteps: InitializationStepStatus[]) {
    const statusMap = new Map(statusSteps.map(s => [s.key, s]));
    steps.value = STEP_DEFINITIONS.map(def => ({
      key: def.key,
      icon: def.icon,
      route: def.route,
      optional: def.optional,
      // Getters: the wording is read when the dialog renders, not copied into the ref at fetch time.
      get title() { return translate(def.titleKey); },
      get description() { return translate(def.descriptionKey); },
      completed: statusMap.get(def.key)?.completed ?? false,
    }));
  }

  async function loadStatus(options?: {
    markLoaded?: boolean;
    showWelcomeWhenEmpty?: boolean;
  }) {
    if (!isAdmin.value) return;
    loading.value = true;
    try {
      const response = await customSdk.getInitializationStatus();
      mergeStatus(response.steps);

      if (options?.markLoaded) {
        loaded.value = true;
      }

      // Show welcome dialog only if no step has been completed yet
      if (options?.showWelcomeWhenEmpty && !anyCompleted.value) {
        showWelcomeDialog.value = true;
      }
    } catch {
      // Silently fail — wizard is non-critical
    } finally {
      loading.value = false;
    }
  }

  async function fetchStatus() {
    await loadStatus({ markLoaded: true, showWelcomeWhenEmpty: true });
  }

  async function refreshStatus() {
    await loadStatus();
  }

  function openStepsDialog() {
    showStepsDialog.value = true;
    refreshStatus();
  }

  function closeStepsDialog() {
    showStepsDialog.value = false;
  }

  function startConfiguration() {
    showWelcomeDialog.value = false;
    openStepsDialog();
  }

  function navigateToStep(step: WizardStep) {
    showStepsDialog.value = false;
    router.push(step.route);
  }

  // Watch for isAdmin to become true (sdk.me loads asynchronously after auth)
  watch(isAdmin, (admin) => {
    if (admin && !loaded.value) {
      fetchStatus();
    }
  }, { immediate: true });

  return {
    steps,
    loading,
    loaded,
    isAdmin,
    completedCount,
    requiredCount,
    requiredCompletedCount,
    totalCount,
    allCompleted,
    anyCompleted,
    showFab,
    showWelcomeDialog,
    showStepsDialog,
    dismissed,
    openStepsDialog,
    closeStepsDialog,
    startConfiguration,
    navigateToStep,
    refreshStatus,
    dismissWizard,
  };
}
